import React, { useState, useEffect } from 'react'
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  TextField,
  Typography,
  Chip,
} from '@mui/material'
import { Close, Add, Delete } from '@mui/icons-material'
import {
  useCreateWeeklyContextMutation,
  useGetActiveWeeklyContextQuery,
} from 'graphql/generated/graphql'
import { errorMessage, successMessage } from 'components/toast/toast'

interface WeeklyContextModalProps {
  open: boolean
  onClose: () => void
  onSuccess?: () => void
}

interface WeeklyContextData {
  books: string[]
  activities: string[]
  media: string[]
  topics: string[]
  freeform: string
}

const CategoryInput: React.FC<{
  label: string
  placeholder: string
  items: string[]
  onAdd: (item: string) => void
  onRemove: (index: number) => void
}> = ({ label, placeholder, items, onAdd, onRemove }) => {
  const [inputValue, setInputValue] = useState('')

  const handleAdd = () => {
    if (inputValue.trim()) {
      onAdd(inputValue.trim())
      setInputValue('')
    }
  }

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      e.preventDefault()
      handleAdd()
    }
  }

  return (
    <Box sx={{ mb: 3 }}>
      <Typography sx={{ color: '#fff', mb: 1, fontWeight: 500 }}>{label}</Typography>
      <Box sx={{ display: 'flex', gap: 1, mb: 1 }}>
        <TextField
          size="small"
          placeholder={placeholder}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyPress={handleKeyPress}
          sx={{
            flex: 1,
            '& .MuiOutlinedInput-root': {
              color: '#fff',
              '& fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
              '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.5)' },
              '&.Mui-focused fieldset': { borderColor: '#a855f7' },
            },
            '& .MuiInputBase-input::placeholder': { color: 'rgba(255,255,255,0.5)' },
          }}
        />
        <Button
          variant="outlined"
          onClick={handleAdd}
          sx={{
            minWidth: 'auto',
            px: 2,
            borderColor: 'rgba(255,255,255,0.3)',
            color: '#fff',
            '&:hover': { borderColor: '#a855f7', bgcolor: 'rgba(168,85,247,0.1)' },
          }}
        >
          <Add fontSize="small" />
        </Button>
      </Box>
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
        {items.map((item, index) => (
          <Chip
            key={index}
            label={item}
            onDelete={() => onRemove(index)}
            deleteIcon={<Delete sx={{ color: 'rgba(255,255,255,0.7) !important' }} />}
            sx={{
              bgcolor: 'rgba(168,85,247,0.2)',
              color: '#fff',
              '& .MuiChip-deleteIcon:hover': { color: '#ff5252 !important' },
            }}
          />
        ))}
      </Box>
    </Box>
  )
}

export default function WeeklyContextModal({
  open,
  onClose,
  onSuccess,
}: WeeklyContextModalProps): JSX.Element {
  const [contextData, setContextData] = useState<WeeklyContextData>({
    books: [],
    activities: [],
    media: [],
    topics: [],
    freeform: '',
  })

  const { data: existingContext, refetch } = useGetActiveWeeklyContextQuery({
    skip: !open,
  })

  const [createWeeklyContext, { loading }] = useCreateWeeklyContextMutation()

  useEffect(() => {
    if (open && existingContext?.getActiveWeeklyContext?.context) {
      const ctx = existingContext.getActiveWeeklyContext.context as WeeklyContextData
      setContextData({
        books: ctx.books || [],
        activities: ctx.activities || [],
        media: ctx.media || [],
        topics: ctx.topics || [],
        freeform: ctx.freeform || '',
      })
    }
  }, [open, existingContext])

  const handleAddItem = (category: keyof Omit<WeeklyContextData, 'freeform'>) => (item: string) => {
    setContextData((prev) => ({
      ...prev,
      [category]: [...prev[category], item],
    }))
  }

  const handleRemoveItem = (category: keyof Omit<WeeklyContextData, 'freeform'>) => (index: number) => {
    setContextData((prev) => ({
      ...prev,
      [category]: prev[category].filter((_, i) => i !== index),
    }))
  }

  const handleSave = async () => {
    try {
      console.log('Saving weekly context with data:', contextData)

      const result = await createWeeklyContext({
        variables: {
          context: {
            books: contextData.books,
            activities: contextData.activities,
            media: contextData.media,
            topics: contextData.topics,
            freeform: contextData.freeform,
          },
        },
      })

      console.log('Weekly context mutation result:', result)

      if (result.data?.createWeeklyContext) {
        successMessage('Weekly context saved successfully!')
        await refetch()
        onSuccess?.()
        onClose()
      } else {
        console.error('Weekly context mutation returned false. Result:', result)
        console.error('Errors:', result.errors)
        errorMessage('Failed to save weekly context')
      }
    } catch (err) {
      console.error('Error saving weekly context:', err)
      console.error('Error details:', JSON.stringify(err, Object.getOwnPropertyNames(err)))
      errorMessage('Failed to save weekly context. Check console for details.')
    }
  }

  const handleClose = () => {
    setContextData({
      books: [],
      activities: [],
      media: [],
      topics: [],
      freeform: '',
    })
    onClose()
  }

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="sm"
      fullWidth
      PaperProps={{
        sx: {
          bgcolor: '#1a1a2e',
          color: '#fff',
          borderRadius: 2,
          maxHeight: '90vh',
        },
      }}
    >
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          borderBottom: '1px solid rgba(255,255,255,0.1)',
        }}
      >
        <Typography variant="h6" sx={{ fontWeight: 600 }}>
          What&apos;s Been On Your Mind This Week?
        </Typography>
        <IconButton onClick={handleClose} sx={{ color: '#fff' }}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent sx={{ mt: 2 }}>
        <Typography sx={{ color: 'rgba(255,255,255,0.7)', mb: 3 }}>
          Share what you&apos;ve been up to recently. This helps make your AI-generated comments
          more personal and organic.
        </Typography>

        <CategoryInput
          label="Books or Articles Read"
          placeholder="e.g., Atomic Habits"
          items={contextData.books}
          onAdd={handleAddItem('books')}
          onRemove={handleRemoveItem('books')}
        />

        <CategoryInput
          label="Activities or Hobbies"
          placeholder="e.g., Started rock climbing"
          items={contextData.activities}
          onAdd={handleAddItem('activities')}
          onRemove={handleRemoveItem('activities')}
        />

        <CategoryInput
          label="Shows or Movies Watched"
          placeholder="e.g., The Bear S3"
          items={contextData.media}
          onAdd={handleAddItem('media')}
          onRemove={handleRemoveItem('media')}
        />

        <CategoryInput
          label="Topics of Interest"
          placeholder="e.g., AI ethics, Photography"
          items={contextData.topics}
          onAdd={handleAddItem('topics')}
          onRemove={handleRemoveItem('topics')}
        />

        <Box sx={{ mb: 2 }}>
          <Typography sx={{ color: '#fff', mb: 1, fontWeight: 500 }}>
            Anything else on your mind?
          </Typography>
          <TextField
            multiline
            rows={3}
            placeholder="What's your current vibe? Any thoughts you want your comments to reflect?"
            value={contextData.freeform}
            onChange={(e) => setContextData((prev) => ({ ...prev, freeform: e.target.value }))}
            fullWidth
            sx={{
              '& .MuiOutlinedInput-root': {
                color: '#fff',
                '& fieldset': { borderColor: 'rgba(255,255,255,0.3)' },
                '&:hover fieldset': { borderColor: 'rgba(255,255,255,0.5)' },
                '&.Mui-focused fieldset': { borderColor: '#a855f7' },
              },
              '& .MuiInputBase-input::placeholder': { color: 'rgba(255,255,255,0.5)' },
            }}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
        <Button onClick={handleClose} sx={{ color: 'rgba(255,255,255,0.7)' }}>
          Cancel
        </Button>
        <Button
          onClick={() => { void handleSave() }}
          variant="contained"
          disabled={loading}
          sx={{
            background: 'linear-gradient(135deg, #a855f7 0%, #ec4899 100%)',
            '&:hover': {
              background: 'linear-gradient(135deg, #9333ea 0%, #db2777 100%)',
            },
          }}
        >
          {loading ? 'Saving...' : 'Save This Week'}
        </Button>
      </DialogActions>
    </Dialog>
  )
}
